import type { ChangesInbox, TaskSummary, WikiChangeSet, WikiReviewItem } from "@esi/shared";
import { isOverdue } from "../../tasks/parts.tsx";
import { time } from "./what-changed.ts";

/** How many rows Needs you lists; the heading carries the total. */
export const NEEDS_YOU_ROWS = 8;

/** One thing that waits for the viewer, newest first (overdue tasks on top). */
export type NeedsYouRow =
  /** A change set the viewer may review, or one of theirs sent back with changes requested. */
  | { kind: "changeSet"; id: string; at: string; set: WikiChangeSet; sentBack: boolean }
  /** A review item the viewer must act on (a source changed, a re-review). */
  | { kind: "reviewItem"; id: string; at: string; item: WikiReviewItem }
  /** One of the viewer's open tasks. */
  | { kind: "task"; id: string; at: string; task: TaskSummary; overdue: boolean }
  /** Managers: the pending memory candidates, as one row. */
  | { kind: "memories"; id: string; at: string; count: number }
  /** Managers: the open access requests, as one row. */
  | { kind: "accessRequests"; id: string; at: string; count: number };

export interface NeedsYouSources {
  /** The review queue grouped for the viewer; null when they may not see drafts. */
  review: ChangesInbox | null;
  /** The viewer's open tasks (`assignee=me&status=open`); hidden ones keep their due date. */
  tasks: readonly TaskSummary[];
  /** `memory.manage` only: the queue's size and its newest candidate's date. */
  memories: { count: number; at: string } | null;
  /** `members.manage` only: the open requests' count and the newest one's date. */
  accessRequests: { count: number; at: string } | null;
  /** Today as `YYYY-MM-DD` (UTC, as task due dates are compared everywhere). */
  today?: string;
}

/**
 * Every row of Needs you, ordered: overdue tasks first (the most overdue on top), then the rest
 * newest first. Built only from responses already authorized for the viewer; the review rows
 * follow the review count's own predicate (`changesInbox().toDecide`), plus the viewer's own
 * change sets sent back to them.
 */
export function needsYouRows(sources: NeedsYouSources): NeedsYouRow[] {
  const rows: NeedsYouRow[] = [];
  const seen = new Set<string>();
  const { review } = sources;
  if (review) {
    const me = review.viewer.userId.toLowerCase();
    for (const { set } of review.toDecide.changeSets) {
      seen.add(set.id);
      rows.push({
        kind: "changeSet",
        id: set.id,
        at: set.submittedAt ?? set.updatedAt,
        set,
        sentBack: set.status === "changes_requested" && set.createdBy.toLowerCase() === me,
      });
    }
    for (const { set } of review.mine.changeSets) {
      if (set.status === "changes_requested" && !seen.has(set.id)) {
        rows.push({
          kind: "changeSet",
          id: set.id,
          at: set.reviewCommentAt ?? set.updatedAt,
          set,
          sentBack: true,
        });
      }
    }
    for (const item of review.toDecide.items) {
      rows.push({ kind: "reviewItem", id: item.id, at: item.createdAt, item });
    }
  }
  for (const task of sources.tasks) {
    rows.push({
      kind: "task",
      id: task.id,
      at: task.hidden ? "" : task.updatedAt,
      task,
      overdue: isOverdue(task.dueDate, task.status, sources.today),
    });
  }
  if (sources.memories && sources.memories.count > 0) {
    rows.push({ kind: "memories", id: "memories", ...sources.memories });
  }
  if (sources.accessRequests && sources.accessRequests.count > 0) {
    rows.push({
      kind: "accessRequests",
      id: "access-requests",
      ...sources.accessRequests,
    });
  }
  return rows.sort((a, b) => {
    const aOverdue = a.kind === "task" && a.overdue;
    const bOverdue = b.kind === "task" && b.overdue;
    if (aOverdue !== bOverdue) {
      return aOverdue ? -1 : 1;
    }
    if (aOverdue && a.kind === "task" && b.kind === "task") {
      // Both overdue: the one due first is the most overdue.
      return (a.task.dueDate ?? "").localeCompare(b.task.dueDate ?? "");
    }
    return time(b.at) - time(a.at);
  });
}
